import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import SUTextField from '../../Components/SUTextField';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import TriggerTypeField from '../../Components/TriggerTypeField';
import useNodeEditor from '../../Components/useNodeEditor';
import ValueFiltersEditor from '../../Components/ValueFiltersEditor';
import { renderVariableChips } from '../../Components/VariableChips';
import { NODE_THEMES } from '../../Components/NodeTheme';
import { FieldLabel, ChipButton, ListRow, EmptyHint } from '../../Components/NodeFormKit';
import NodeModal, { NodeModalButton } from '../../Components/NodeModal';
import Editor from '@monaco-editor/react';

import styledComponent from 'styled-components';
import Select from 'react-select';

import { LuFileJson } from 'react-icons/lu';

const WEBHOOK_NODE_COLOR = '#FC6530';

// El motor no permite que un evento externo cambie la identidad del contacto
const BLOCKED_DESTINATIONS = ['{{telefono_usuario}}', '{{phone}}', '{{bsuid_usuario}}', '{{bsuid}}'];

const MAP_SOURCE_OPTIONS = [
  { label: 'Dato del evento', value: 'user_message' },
  { label: 'Asignar valor fijo manual', value: 'manual' },
  { label: 'Limpiar / Vaciar campo', value: 'clear' },
];

const parseStates = (text) =>
  String(text || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const describeMapping = (row) => {
  const parts = [];
  if (row.source === 'manual') parts.push(`Valor fijo: ${row.manualValue || '(vacío)'}`);
  if (row.source === 'clear') parts.push('Limpia el campo');
  const filters = (row.filters || []).length;
  if (filters) parts.push(`${filters} filtro${filters > 1 ? 's' : ''}`);
  const states = row.when?.values || [];
  parts.push(states.length ? `Solo en: ${states.join(', ')}` : 'En todos los eventos');
  return parts.join(' · ');
};

const WebHook = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  watchSelectedNode,
  contextVariables,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({});

  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;

  // ------> Selects, Radios, Checkbox
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const { handleInputChange, loadData, saveNode, getIsDirty } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });
  const [showAdvanced, setShowAdvanced] = useState(
    () => !!(selectedNode?.data?.lock_rules?.length || selectedNode?.data?.responseParams?.length)
  );
  const [showLockSection, setShowLockSection] = useState(
    () => !!(selectedNode?.data?.lock_rules?.length)
  );
  const [showMapSection, setShowMapSection] = useState(
    () => !!(selectedNode?.data?.responseParams?.length)
  );
  const [mapModal, setMapModal] = useState(false);
  const [mapIndex, setMapIndex] = useState(null);
  const [mapFilters, setMapFilters] = useState([]);
  const [mapStatesText, setMapStatesText] = useState('');
  const [mapError, setMapError] = useState(null);

  const [mapSource, setMapSource] = useState('user_message');

  const webhookVariables = (contextVariables || []).filter((context) => context.id === selectedNode?.id);
  const statusProperty = selectedNode?.data?.status_format_property;
  // Mismo tamaño que los campos de SUTextField para que el modal se vea parejo
  const fieldLabelStyle = { display: 'block', color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '5px' };
  const fieldInputStyle = {
    height: '42px',
    borderRadius: '8px',
    fontSize: '13px',
    padding: '2px 10px',
    backgroundColor: theme.inputBg,
    border: `1px solid ${theme.border}`,
    color: theme.textPrimary,
  };

  // ── Mapeo de respuesta: guardar datos del evento en variables del contacto ──
  const openMapModal = (index = null) => {
    const row = index !== null ? (selectedNode.data?.responseParams || [])[index] : null;
    setValue('map_key', row?.key ?? '');
    setValue('map_value', row?.value ?? '');
    setMapSource(row?.source ?? 'user_message');
    setValue('map_manualValue', row?.manualValue ?? '');
    setMapFilters(Array.isArray(row?.filters) ? row.filters : []);
    setMapStatesText((row?.when?.values || []).join(', '));
    setMapError(null);
    setMapIndex(index);
    setMapModal(true);
  };

  const closeMapModal = () => {
    setMapModal(false);
    setMapIndex(null);
    setMapError(null);
  };

  const saveMapping = () => {
    const rawKey = String(getValues('map_key') || '').trim();
    const key = rawKey && !rawKey.includes('{{') ? `{{${selectedNode.id}.${rawKey}}}` : rawKey;
    const value = String(getValues('map_value') || '').trim();
    const source = mapSource;

    if (source === 'user_message' && !key) return setMapError('Elige de dónde sale el dato.');
    if (!value) return setMapError('Elige la variable donde se guarda.');
    if (BLOCKED_DESTINATIONS.includes(value)) {
      return setMapError('El teléfono del contacto no se puede cambiar desde un webhook.');
    }

    const states = parseStates(mapStatesText);
    const row = {
      key: source === 'user_message' ? key : '',
      value,
      source,
      manualValue: source === 'manual' ? getValues('map_manualValue') || '' : '',
      filters: source === 'user_message' ? mapFilters : [],
      ...(states.length ? { when: { values: states } } : {}),
    };

    const current = [...(selectedNode.data?.responseParams || [])];
    if (mapIndex === null) current.push(row);
    else current[mapIndex] = row;
    handleSelectedNode({ ...selectedNode, data: { ...selectedNode.data, responseParams: current } });
    closeMapModal();
  };

  const removeMapping = (index) => {
    const current = selectedNode.data?.responseParams || [];
    handleSelectedNode({
      ...selectedNode,
      data: { ...selectedNode.data, responseParams: current.filter((_, i) => i !== index) },
    });
  };

  // ── Lock rules helpers — usa selectedNode directamente (no functional update) ──
  const addLockRule = () => {
    const current = selectedNode.data?.lock_rules || [];
    handleSelectedNode({
      ...selectedNode,
      data: {
        ...selectedNode.data,
        lock_rules: [...current, { event_field: '', lock_on_value: '', unlock_event_field: '', unlock_values: [], timeout_minutes: 240 }],
      },
    });
  };

  const removeLockRule = (e, index) => {
    e.stopPropagation();
    e.preventDefault();
    const current = selectedNode.data?.lock_rules || [];
    handleSelectedNode({
      ...selectedNode,
      data: { ...selectedNode.data, lock_rules: current.filter((_, i) => i !== index) },
    });
  };

  const updateLockRule = (index, field, value) => {
    const current = selectedNode.data?.lock_rules || [];
    handleSelectedNode({
      ...selectedNode,
      data: {
        ...selectedNode.data,
        lock_rules: current.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
      },
    });
  };
  // ────────────────────────────────────────────────────────────────────────

  // FUNCTIONS

  //# Function para manejar cambios de estados de campos

  //# Function para guardar los datos
  const saveData = (values) => {
    setLoadingUpdate(true);

    let bodyValue = values.body;
    if (typeof bodyValue === 'object') {
      try {
        bodyValue = JSON.stringify(bodyValue, null, 2);
      } catch (e) {}
    }

    const data = {
      title: values.title,
      user_response: values.user_response,
      trigger_type: selectedNode.data?.trigger_type || 'message',
      url: values.url,
      body: bodyValue,
      variables: JSON.parse(bodyValue),
      phone_format_property: values.phone_format_property,
      status_format_property: values.status_format_property,
      dedupe_key_property: values.dedupe_key_property,
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
    });
  };

  //# Function para obtner data
  const getData = () => {
    let bodyValue = selectedNode.data?.body ?? null;
    // Si es string y es JSON válido, lo formateamos bonito
    if (typeof bodyValue === 'string') {
      try {
        const parsed = JSON.parse(bodyValue);
        bodyValue = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Si no es JSON válido, lo dejamos como está
      }
    }
    let objectData = {
      title: selectedNode.data?.title ?? null,
      user_response: selectedNode.data?.user_response ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
      url: selectedNode.data?.url ?? null,
      body: bodyValue,
      phone_format_property: selectedNode.data?.phone_format_property ?? null,
      status_format_property: selectedNode.data?.status_format_property ?? null,
      dedupe_key_property: selectedNode.data?.dedupe_key_property ?? null,
    };
    loadData(objectData);
  };

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  return (
    <>
    <NodeEditorChrome
      icon={LuFileJson}
      color={WEBHOOK_NODE_COLOR}
      typeName="WebHook"
      title={truncateText(selectedNode.data.title)}
      onRemove={() => removeNode(selectedNode.id)}
      onMouseDown={onMouseDown}
      isDragging={isDragging}
      onClose={() => handleSelectedNode(null)}
      formId="webHookForm"
      saving={loadingUpdate}
      isDark={isDark}
      getIsDirty={getIsDirty}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    >
        <form id="webHookForm" onSubmit={handleSubmit(saveData)} style={{ width: '100%' }}>
          <div>
            <div className="col p-0">
              <SUTextField
                id="title"
                name="title"
                label="Título:"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('title', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            <div className="col p-0 mt-4">
              <SUTextField
                id="user_response"
                name="user_response"
                label="Respuesta del cliente:"
                hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ."
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('user_response', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>
            <div className="col p-0 mt-4">
              <SUTextField
                id="url"
                name="url"
                label="URL:"
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                readOnly={true}
                allowCopy={true}
                {...register('url', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            <div className="col p-0 mt-4">
              <FieldLabel>Propiedad del número de teléfono:</FieldLabel>
              <SUTextField
                id="phone_format_property"
                name="phone_format_property"
                label="Propiedad (ej: phone, celular, numero):"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('phone_format_property', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            <div className="col p-0 mt-4">
              <FieldLabel>Propiedad del estado (opcional):</FieldLabel>
              <SUTextField
                id="status_format_property"
                name="status_format_property"
                label="Propiedad (ej: status, estado, situacion):"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('status_format_property', {
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
              <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
                Campo del evento que trae el estado (ej: serviceStatus). Solo hace falta en bots
                transaccionales o si usas condiciones en &quot;Guardar en variables&quot;.
              </p>
            </div>
            <div className="col p-0 mt-4">
              <FieldLabel>Propiedad(es) para detectar duplicados (opcional):</FieldLabel>
              <SUTextField
                id="dedupe_key_property"
                name="dedupe_key_property"
                label="Separadas por coma (ej: travelId, status):"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('dedupe_key_property')}
              />
              <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
                Si el proveedor reenvía el mismo evento más de una vez, estas propiedades
                identifican cuándo dos llamadas son el mismo evento para no repetir la
                respuesta. Si se deja vacío, se compara el cuerpo completo de la petición.
              </p>
            </div>
            <div className="col p-0 mt-4">
              <FieldLabel>Cuerpo del query:</FieldLabel>
              <div
                style={{
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  marginTop: 4,
                  background: isDark ? '#111b21' : '#fff',
                  overflow: 'hidden',
                  resize: 'vertical',
                  minHeight: 120,
                  maxHeight: 600,
                  height: 120,
                  width: '100%',
                  position: 'relative',
                }}
              >
                <Editor
                  height="100%"
                  width="100%"
                  defaultLanguage="json"
                  value={getValues('body') || ''}
                  theme={isDark ? 'vs-dark' : 'light'}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    formatOnPaste: true,
                    formatOnType: true,
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    lineNumbersMinChars: 2,
                    lineDecorationsWidth: 2,
                    guides: {
                      indentation: true,
                      highlightActiveIndentation: true,
                    },
                    suggestOnTriggerCharacters: true,
                    quickSuggestions: true,
                    autoClosingBrackets: 'always',
                    autoClosingQuotes: 'always',
                    bracketPairColorization: { enabled: true },
                    matchBrackets: 'always',
                    tabSize: 2,
                  }}
                  onChange={(value) =>
                    setValue('body', value, { shouldDirty: true })
                  }
                />
                {errors?.body && (
                  <span style={{ color: 'red', fontSize: 12 }}>
                    * {errors.body.message}
                  </span>
                )}
              </div>
            </div>
          </div>

            {/* ── Opciones avanzadas ─────────────────────────────────────── */}
            <div className="col p-0 mt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: theme.textSecondary,
                  fontSize: 13, fontWeight: 600,
                }}
              >
                <span style={{
                  display: 'inline-block', transition: 'transform 0.2s',
                  transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)',
                  fontSize: 12,
                }}>▶</span>
                Opciones avanzadas
              </button>

              {showAdvanced && (
                <div style={{ marginTop: 12 }}>

                  {/* Sub-sección: Bloqueo de conversación */}
                  <div style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowLockSection((v) => !v)}
                      style={{
                        width: '100%', textAlign: 'left', background: theme.inputBg,
                        border: 'none', padding: '8px 12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        color: theme.textSecondary,
                        fontSize: 12, fontWeight: 600,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          display: 'inline-block', transition: 'transform 0.2s', fontSize: 12,
                          transform: showLockSection ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}>▶</span>
                        Bloqueo de conversación
                      </span>
                      {(selectedNode.data?.lock_rules?.length > 0) && (
                        <span style={{
                          background: '#FC6530', color: '#fff', borderRadius: 10,
                          fontSize: 12, padding: '0 6px', lineHeight: '16px',
                        }}>
                          {selectedNode.data.lock_rules.length}
                        </span>
                      )}
                    </button>

                    {showLockSection && (
                      <div style={{ padding: '10px 12px' }}>
                        <p style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 10 }}>
                          Cuando llegue un evento específico, el bot ignora los mensajes del cliente hasta que llegue el evento de desbloqueo o expire el tiempo.
                        </p>
                        {(selectedNode.data?.lock_rules || []).map((rule, index) => (
                          <div
                            key={index}
                            style={{
                              border: `1px solid ${isDark === 'true' ? '#2a3942' : '#e3e9ec'}`,
                              borderRadius: 6,
                              padding: '10px 12px',
                              marginBottom: 8,
                              position: 'relative',
                              backgroundColor: isDark === 'true' ? '#111b21' : '#f4fafc',
                            }}
                          >
                            <button
                              type="button"
                              onClick={(e) => removeLockRule(e, index)}
                              style={{
                                position: 'absolute', top: 6, right: 8,
                                background: 'none', border: 'none',
                                color: '#E5253D', cursor: 'pointer', fontSize: 16, lineHeight: 1,
                                zIndex: 2,
                              }}
                            >
                              ✕
                            </button>
                            {/* Fila de encabezados */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#FC6530', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Bloqueo
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Desbloqueo
                              </span>
                            </div>

                            {/* Campo */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                              <div>
                                <label style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 2, display: 'block' }}>
                                  Campo
                                </label>
                                <input
                                  className="form-control"
                                  style={{ height: 30, fontSize: 12 }}
                                  placeholder="ej: source"
                                  value={rule.event_field || ''}
                                  onChange={(e) => updateLockRule(index, 'event_field', e.target.value)}
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 2, display: 'block' }}>
                                  Campo
                                </label>
                                <input
                                  className="form-control"
                                  style={{ height: 30, fontSize: 12 }}
                                  placeholder="ej: serviceStatus"
                                  value={rule.unlock_event_field || ''}
                                  onChange={(e) => updateLockRule(index, 'unlock_event_field', e.target.value)}
                                />
                              </div>
                            </div>

                            {/* Valor */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                              <div>
                                <label style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 2, display: 'block' }}>
                                  Valor
                                </label>
                                <input
                                  className="form-control"
                                  style={{ height: 30, fontSize: 12 }}
                                  placeholder="ej: call"
                                  value={rule.lock_on_value ?? ''}
                                  onChange={(e) => updateLockRule(index, 'lock_on_value', e.target.value)}
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 2, display: 'block' }}>
                                  Valores (coma)
                                </label>
                                <input
                                  className="form-control"
                                  style={{ height: 30, fontSize: 12 }}
                                  placeholder="ej: FINISHED"
                                  value={(rule.unlock_values || []).join(', ')}
                                  onChange={(e) =>
                                    updateLockRule(index, 'unlock_values',
                                      e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                                    )
                                  }
                                />
                              </div>
                            </div>

                            {/* Timeout */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                              <div>
                                <label style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 2, display: 'block' }}>
                                  Timeout (min)
                                </label>
                                <input
                                  type="number"
                                  className="form-control"
                                  style={{ height: 30, fontSize: 12 }}
                                  min={1}
                                  value={rule.timeout_minutes || 240}
                                  onChange={(e) => updateLockRule(index, 'timeout_minutes', parseInt(e.target.value) || 240)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <ChipButton onClick={addLockRule}>
                          + Agregar regla
                        </ChipButton>
                      </div>
                    )}
                  </div>

                  {/* Sub-sección: Guardar en variables */}
                  <div style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                    marginTop: 10,
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowMapSection((v) => !v)}
                      style={{
                        width: '100%', textAlign: 'left', background: theme.inputBg,
                        border: 'none', padding: '8px 12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        color: theme.textSecondary,
                        fontSize: 12, fontWeight: 600,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          display: 'inline-block', transition: 'transform 0.2s', fontSize: 12,
                          transform: showMapSection ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}>▶</span>
                        Guardar en variables
                      </span>
                      {(selectedNode.data?.responseParams?.length > 0) && (
                        <span style={{
                          background: WEBHOOK_NODE_COLOR, color: '#fff', borderRadius: 10,
                          fontSize: 12, padding: '0 6px', lineHeight: '16px',
                        }}>
                          {selectedNode.data.responseParams.length}
                        </span>
                      )}
                    </button>

                    {showMapSection && (
                      <div style={{ padding: '10px 12px' }}>
                        <p style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 10 }}>
                          Guarda datos del evento en variables del contacto. Si el evento no trae el dato, la variable conserva su valor.
                        </p>
                        {(selectedNode.data?.responseParams || []).length ? (
                          (selectedNode.data.responseParams || []).map((row, index) => (
                            <ListRow key={index} onRemove={() => removeMapping(index)} removeTitle="Eliminar">
                              <div
                                className="pointer"
                                title="Editar"
                                style={{ overflowWrap: 'anywhere' }}
                                onClick={() => openMapModal(index)}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {row.source === 'user_message' ? renderVariableChips(row.key) : '—'}
                                </div>
                                <div style={{ color: theme.textSecondary }}>→ {renderVariableChips(row.value)}</div>
                                <div style={{ fontSize: 12, color: WEBHOOK_NODE_COLOR, marginTop: 2 }}>
                                  {describeMapping(row)}
                                </div>
                              </div>
                            </ListRow>
                          ))
                        ) : (
                          <EmptyHint>Sin variables mapeadas.</EmptyHint>
                        )}
                        <div className="mt-2">
                          <ChipButton onClick={() => openMapModal()}>
                            + Agregar variable
                          </ChipButton>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
        </form>
    </NodeEditorChrome>

    <NodeModal
      open={mapModal}
      onClose={closeMapModal}
      width={500}
      isDark={isDark}
      icon={LuFileJson}
      color={WEBHOOK_NODE_COLOR}
      eyebrow="WebHook"
      title={mapIndex === null ? 'Guardar en variable' : 'Editar variable'}
    >
      {mapModal && (
        <>
        <style>{`.wh-map-input::placeholder { font-style: normal; }`}</style>
        <div>
          <div className="col">
            <label style={fieldLabelStyle}>Origen del dato:</label>
            <div className="su-control-group">
              <Select
                styles={isDark == 'true' ? customStylesDark : customStyles}
                className="form-control basic-multi-select"
                classNamePrefix="select"
                isSearchable={false}
                value={MAP_SOURCE_OPTIONS.find((option) => option.value === mapSource)}
                onChange={(option) => setMapSource(option.value)}
                options={MAP_SOURCE_OPTIONS}
                getOptionValue={(option) => option.value}
              />
            </div>
          </div>

          {mapSource === 'user_message' && (
            <div className="col mt-3">
              <SUTextField
                id="map_key"
                name="map_key"
                label="Dato del evento:"
                type="text"
                hint="Elige un campo del cuerpo del webhook. También acepta plantillas y concatenación con +."
                showVariables
                typeVariables={['api']}
                contextVariables={webhookVariables}
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('map_key')}
              />
            </div>
          )}

          {mapSource === 'manual' && (
            <div className="col mt-3">
              <label style={fieldLabelStyle}>Valor fijo:</label>
              <input
                type="text"
                className="form-control wh-map-input"
                placeholder="Valor fijo a asignar..."
                style={fieldInputStyle}
                {...register('map_manualValue')}
              />
            </div>
          )}

          <div className="col mt-3">
            <SUTextField
              id="map_value"
              name="map_value"
              label="Variable de destino (donde se guarda):"
              type="text"
              showVariables
              typeVariables={['general']}
              contextVariables={contextVariables}
              selectedNode={selectedNode}
              getValues={getValues}
              setValue={setValue}
              errors={errors}
              {...register('map_value')}
            />
          </div>

          {mapSource === 'user_message' && (
            <div className="col">
              <ValueFiltersEditor
                filters={mapFilters}
                onChange={setMapFilters}
                isDark={isDark}
                accentColor={WEBHOOK_NODE_COLOR}
              />
            </div>
          )}

          <div className="col mt-3 pt-3 pb-2" style={{ borderTop: `1px solid ${theme.border}` }}>
            <label style={fieldLabelStyle}>Condición (opcional):</label>
            {statusProperty ? (
              <>
                <input
                  type="text"
                  className="form-control wh-map-input"
                  placeholder="ej: ACCEPTED, ARRIVED"
                  style={fieldInputStyle}
                  value={mapStatesText}
                  onChange={(e) => setMapStatesText(e.target.value)}
                />
                <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4, marginBottom: 0 }}>
                  Solo se guarda cuando <b>{statusProperty}</b> sea alguno de estos valores (separados por coma). Vacío = en todos los eventos.
                </p>
              </>
            ) : (
              <p style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 0 }}>
                Configura la <b>Propiedad del estado</b> del webhook para poder usar condiciones.
              </p>
            )}
          </div>
        </div>

        <div>
          {mapError && (
            <div className="col mt-2" style={{ color: '#E5253D', fontSize: 12 }}>* {mapError}</div>
          )}

          <div className="d-flex justify-content-end mt-3" style={{ gap: '8px' }}>
            <NodeModalButton isDark={isDark} onClick={closeMapModal}>
              Cancelar
            </NodeModalButton>
            <NodeModalButton
              variant="primary"
              color={WEBHOOK_NODE_COLOR}
              isDark={isDark}
              onClick={saveMapping}
            >
              {mapIndex === null ? 'Agregar' : 'Guardar'}
            </NodeModalButton>
          </div>
        </div>
        </>
      )}
    </NodeModal>
    </>
  );
};

export default WebHook;
